import { LinearIcon } from "@storytell/chat-ui/tools";
import { TbCheck } from "solid-icons/tb";
import { type Component, For, type JSX, Show } from "solid-js";
import { BrandGlyph } from "~/components/marketing/BrandGlyph";
import { useScene } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import {
  NeedsCard,
  NeedsGroup,
  OfferFoot,
  Swap,
  type TranscriptEntry,
  TranscriptBlock,
  ViewTab,
} from "./internal/chief";
import { MeetingNotchCard, NotchLive } from "./internal/notch";
import { at, Cursor, type Person } from "./internal/parts";
import "./mockups.css";

export type ListenFlowData = {
  steps: { title: string; body: string }[];
  clock: string;
  people: Person[];
  lines: TranscriptEntry[];
  offer: string;
  filed: string;
};

// Seconds into the scene. Each moment lights up for its stretch; the connector
// to the next one fills as it hands over, and the step's number turns to a check.
const STEP_START = [0, 2.6, 5.4];
const DONE_AT = [2.4, 5.2, 7.8];
const RAIL_AT = [2.2, 5.0];
const T = {
  startCursor: 0.2,
  start: 1.3,
  live: 1.5,
  lines: [3.0, 3.9],
  offer: 5.8,
  acceptCursor: 6.0,
  accept: 7.1,
  filed: 7.4,
};

/**
 * The three steps of using Chief as one connected picture, played once: the
 * session starts over the call, the transcript fills in, and the work it asked
 * for gets filed. Wide screens set the moments side by side on one stage with
 * the captions in a row under it; narrow screens stack each moment over its
 * caption. The captions stay real text; the moments are pictures drawn with the
 * desktop app's own controls.
 */
export const ListenFlowMockup: Component<{ data: ListenFlowData }> = (props) => {
  const scene = useScene();
  const moments: (() => JSX.Element)[] = [
    () => <CallMoment data={props.data} />,
    () => <TranscriptMoment data={props.data} />,
    () => <OfferMoment offer={props.data.offer} filed={props.data.filed} />,
  ];

  return (
    <ol
      ref={scene.ref}
      class="mk-scene relative flex flex-col gap-10 md:grid md:grid-cols-3 md:gap-0 md:before:absolute md:before:inset-x-0 md:before:top-0 md:before:h-52 md:before:rounded-3xl md:before:border md:before:border-primary-border md:before:bg-tile md:before:bg-[radial-gradient(ellipse_at_50%_100%,color-mix(in_srgb,var(--color-accent)_10%,transparent),transparent_70%),radial-gradient(var(--color-primary-border)_1px,transparent_1px)] md:before:bg-size-[100%_100%,22px_22px]"
      classList={{ "mk-armed": scene.armed(), "in-view": scene.inView() }}
    >
      <For each={props.data.steps}>
        {(step, i) => (
          <li class="relative flex flex-col gap-4 md:row-span-2 md:grid md:grid-rows-subgrid md:gap-0">
            <div
              aria-hidden="true"
              class="mk-app relative flex items-center rounded-3xl border border-primary-border bg-tile p-5 md:h-52 md:rounded-none md:border-0 md:bg-transparent md:px-6 md:py-0 lg:px-8"
            >
              <div class="wake relative w-full" style={at(STEP_START[i()])}>
                <span
                  class="glow-pass pointer-events-none absolute -inset-1.5 rounded-2xl border-2 border-accent shadow-[0_0_40px_-6px_var(--color-accent)]"
                  style={at(STEP_START[i()])}
                />
                {moments[i()]()}
              </div>
              <Show when={i() < props.data.steps.length - 1}>
                <Connector
                  at={RAIL_AT[i()]}
                  class="-inset-e-6 top-1/2 hidden h-0.5 w-12 -translate-y-1/2 md:block lg:-inset-e-8 lg:w-16"
                />
              </Show>
            </div>
            <Show when={i() < props.data.steps.length - 1}>
              <Connector at={RAIL_AT[i()]} vertical class="top-full left-1/2 h-10 w-0.5 -translate-x-1/2 md:hidden" />
            </Show>
            <div class="flex gap-3 px-1 md:px-6 md:pt-5 lg:px-8">
              <span class="relative grid size-6 shrink-0 place-items-center rounded-full border border-primary-border bg-tile font-mono text-xs font-semibold">
                {i() + 1}
                <span
                  class="pop-in absolute -inset-px grid place-items-center rounded-full bg-accent text-white"
                  style={at(DONE_AT[i()])}
                >
                  <TbCheck class="size-3.5" aria-hidden="true" />
                </span>
              </span>
              <div>
                <h3 class="font-semibold">{step.title}</h3>
                <p class="mt-1 text-sm leading-relaxed text-secondary-text">{step.body}</p>
              </div>
            </div>
          </li>
        )}
      </For>
    </ol>
  );
};

const Connector = (props: { at: number; vertical?: boolean; class: string }) => (
  <span aria-hidden="true" class={`absolute rounded-full bg-primary-border ${props.class}`}>
    <span
      class="rail-grow absolute inset-0 rounded-full bg-accent"
      classList={{
        "origin-top [--gx:1] [--gy:0]": props.vertical,
        "origin-left rtl:origin-right [--gx:0] [--gy:1]": !props.vertical,
      }}
      style={at(props.at)}
    />
    <span
      class="travel absolute inset-0"
      classList={{
        "[--ty:-100%]": props.vertical,
        "[--tx:-100%] rtl:[--tx:100%]": !props.vertical,
      }}
      style={at(props.at)}
    >
      <span
        class="absolute size-2.5 rounded-full bg-accent shadow-[0_0_12px_3px_var(--color-accent)]"
        classList={{
          "-bottom-1 left-1/2 -translate-x-1/2": props.vertical,
          "top-1/2 -end-1 -translate-y-1/2": !props.vertical,
        }}
      />
    </span>
  </span>
);

/**
 * Loading bars that hold a moment's view until its content arrives, so a moment
 * waiting its turn reads as about to fill rather than empty. They leave as the
 * content fades in.
 */
const Pending = (props: { until: number }) => (
  <div class="exit absolute inset-0 flex flex-col justify-center gap-2.5 px-4" style={at(props.until - 0.2)}>
    <For each={["w-3/4", "w-1/2", "w-2/3"]}>
      {(width) => (
        <span class={`relative h-2.5 overflow-hidden rounded-full bg-primary-border/70 ${width}`}>
          <span class="shimmer absolute inset-0 bg-linear-to-r from-transparent via-white/70 to-transparent dark:via-white/10" />
        </span>
      )}
    </For>
  </div>
);

/**
 * The call as the meeting app shows it, with only the people in it, and the
 * notch's card asking to start a Live Session folding to its live clock. Each
 * tile rings while its speaker's line comes in next door.
 */
const CallMoment = (props: { data: ListenFlowData }) => (
  <div class="relative">
    <div class="overflow-hidden rounded-xl border border-black/10 bg-mock-window shadow-xl dark:border-white/10">
      <div class="flex h-6 items-center gap-1.5 px-2.5">
        <span class="size-2 rounded-full bg-mock-close" />
        <span class="size-2 rounded-full bg-mock-minimize" />
        <span class="size-2 rounded-full bg-mock-zoom" />
        <BrandGlyph src="/images/logos/zoom.svg" class="ms-auto h-4 w-12 text-white/80" />
      </div>
      <div class="grid grid-cols-2 gap-1 px-1 pb-1">
        <For each={props.data.people}>
          {(person, i) => (
            <div class="relative grid h-24 place-items-center rounded-md bg-mock-video">
              <span
                class="grid size-10 place-items-center rounded-full text-base font-semibold text-white"
                style={{ "background-color": person.color }}
              >
                {person.name[0]}
              </span>
              <span class="absolute inset-s-1.5 bottom-1 rounded-sm bg-black/50 px-1 text-[0.625rem] text-white/90">
                {person.name}
              </span>
              <span
                class="blip absolute inset-0 rounded-md inset-ring-2 inset-ring-mock-speaking"
                style={at((T.lines[i()] ?? 0) - 0.25)}
              />
            </div>
          )}
        </For>
      </div>
    </div>
    <div class="absolute inset-x-0 top-0 flex justify-center">
      <MeetingNotchCard
        pressAt={T.start}
        cursor={<Cursor start={T.startCursor} />}
        class="exit w-60"
        style={at(T.live)}
      />
      <NotchLive clock={props.data.clock} class="card-appear absolute top-0" style={at(T.live)} />
    </div>
  </div>
);

const TranscriptMoment = (props: { data: ListenFlowData }) => {
  const { t } = useI18n();
  return (
    <div class="overflow-hidden rounded-xl border border-primary-border bg-panel shadow-xl">
      <div class="flex h-8 items-center gap-0.5 border-b border-(--cb-divider) px-1.5">
        <ViewTab>{t("marketing.mock.tab.notes")}</ViewTab>
        <ViewTab open>{t("marketing.mock.transcript")}</ViewTab>
      </div>
      <div class="mk-read relative min-h-28 px-3 pt-2.5 pb-0.5">
        <Pending until={T.lines[0]} />
        <For each={props.data.lines}>
          {(line, i) => (
            <TranscriptBlock entry={{ ...line, appearAt: T.lines[i()] ?? 0 }} class="mb-1.5 [&>p]:leading-snug" />
          )}
        </For>
      </div>
    </div>
  );
};

/** Chief's card for the work the call asked for, under Needs you, added from its foot and settling on the result. */
const OfferMoment = (props: { offer: string; filed: string }) => {
  const { t } = useI18n();
  return (
    <div class="relative min-h-24">
      <Pending until={T.offer} />
      <NeedsGroup
        class="card-appear shadow-xl"
        style={at(T.offer)}
        icon={<LinearIcon class="size-3" />}
        label={t("marketing.mock.offer.group")}
        count={1}
      >
        <NeedsCard
          title={props.offer}
          foot={
            <Swap
              swapAt={T.filed}
              before={
                <OfferFoot
                  icon={<LinearIcon class="size-3.5 shrink-0" />}
                  label={t("marketing.mock.offer.add")}
                  pressAt={T.accept}
                  cursor={<Cursor start={T.acceptCursor} />}
                />
              }
              after={
                <span class="flex h-6 items-center gap-1 text-xs text-(--chief-success-ink)">
                  <TbCheck class="size-3.5" aria-hidden="true" />
                  {props.filed}
                </span>
              }
            />
          }
        />
      </NeedsGroup>
    </div>
  );
};
